/**
 * Authentication Store
 * Zustand store for managing user authentication state with localStorage persistence
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { apiService, type RegisterData, type LoginData } from '../lib/api';
import { getSystemContext } from '../lib/systemContext';

export type UserRole = 'STUDENT' | 'TEACHER' | 'EMPLOYEE' | 'ADMIN';

export interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  firstName?: string;
  lastName?: string;
  role: UserRole;
  permissions?: string[];
  schoolId?: string;
  schoolName?: string;
  systemContext?: 'VSAT' | 'THPT'; // System context for multi-system support
  phone?: string;
  createdAt?: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  accessToken: string | null;
  needsVerification: boolean;
  pendingIdentifier: string | null;
  login: (identifier: string, password: string) => Promise<void>;
  loginDashboard: (email: string, password: string) => Promise<User>;
  loginTeacher: (email: string, password: string) => Promise<void>;
  loginEmployee: (email: string, password: string) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  verifyOtp: (identifier: string, otp: string) => Promise<void>;
  resendOtp: (identifier: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithFacebook: () => Promise<void>;
  registerWithGoogle: () => Promise<void>;
  registerWithFacebook: () => Promise<void>;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  clearVerificationState: () => void;
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  hasRole: (role: UserRole) => boolean;
  hasPermission: (permission: string) => boolean;
}

export const OAUTH_STATE_KEY = 'oauthState';

const createOauthState = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `oauth-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const persistOauthState = (value: string) => {
  try {
    localStorage.setItem(OAUTH_STATE_KEY, value);
  } catch (error) {
    console.warn('[authStore] Unable to persist OAuth state', error);
  }
};

const consumeOauthState = () => {
  try {
    const value = localStorage.getItem(OAUTH_STATE_KEY);
    localStorage.removeItem(OAUTH_STATE_KEY);
    return value;
  } catch (error) {
    console.warn('[authStore] Unable to read OAuth state', error);
    return null;
  }
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      accessToken: null,
      needsVerification: false,
      pendingIdentifier: null,

      loginTeacher: async (email: string, password: string) => {
        set({ isLoading: true });

        try {
          const response = await apiService.loginTeacher({ email, password });

          // Store token in localStorage
          localStorage.setItem('accessToken', response.accessToken);

          const user: User = {
            id: response.user.id,
            name: `${response.user.firstName} ${response.user.lastName}`,
            email: response.user.email,
            firstName: response.user.firstName,
            lastName: response.user.lastName,
            role: response.user.role as UserRole,
            permissions: response.user.permissions,
            schoolId: response.user.schoolId,
            schoolName: response.user.schoolName,
          };

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            accessToken: response.accessToken,
            needsVerification: false,
            pendingIdentifier: null,
          });
        } catch (error: any) {
          set({ isLoading: false });
          throw error;
        }
      },

      loginEmployee: async (email: string, password: string) => {
        set({ isLoading: true });

        try {
          const response = await apiService.loginEmployee({ email, password });

          // Store token in localStorage
          localStorage.setItem('accessToken', response.accessToken);

          const user: User = {
            id: response.user.id,
            name: `${response.user.firstName} ${response.user.lastName}`,
            email: response.user.email,
            firstName: response.user.firstName,
            lastName: response.user.lastName,
            role: response.user.role as UserRole,
            permissions: response.user.permissions,
          };

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            accessToken: response.accessToken,
            needsVerification: false,
            pendingIdentifier: null,
          });
        } catch (error: any) {
          set({ isLoading: false });
          throw error;
        }
      },

      loginDashboard: async (email: string, password: string) => {
        set({ isLoading: true });

        try {
          const response = await apiService.loginDashboard({ email, password });

          // Store token in localStorage
          localStorage.setItem('accessToken', response.accessToken);

          const user: User = {
            id: response.user.id,
            name: `${response.user.firstName} ${response.user.lastName}`,
            email: response.user.email,
            firstName: response.user.firstName,
            lastName: response.user.lastName,
            role: response.user.role as UserRole,
            permissions: response.user.permissions,
            schoolId: response.user.schoolId,
            schoolName: response.user.schoolName,
          };

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            accessToken: response.accessToken,
            needsVerification: false,
            pendingIdentifier: null,
          });

          return user;
        } catch (error: any) {
          set({ isLoading: false });
          throw error;
        }
      },

      login: async (identifier: string, password: string) => {
        set({ isLoading: true });

        try {
          // Include systemContext in login request
          const systemContext = getSystemContext();
          const response = await apiService.login({ identifier, password, systemContext });

          // Store token in localStorage
          localStorage.setItem('accessToken', response.accessToken);

          const user: User = {
            id: response.user.id,
            name: `${response.user.firstName} ${response.user.lastName}`,
            email: response.user.email,
            firstName: response.user.firstName,
            lastName: response.user.lastName,
            role: response.user.role as UserRole,
            permissions: response.user.permissions,
          };

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            accessToken: response.accessToken,
            needsVerification: false,
            pendingIdentifier: null,
          });
        } catch (error: any) {
          set({ isLoading: false });

          // Check if it's an unverified account error
          const errorMsg = error?.response?.data?.message || error?.message || '';
          if (errorMsg.includes('unverified') || errorMsg.includes('verify your account')) {
            // Set verification state so the auth page can redirect to OTP verification
            set({
              needsVerification: true,
              pendingIdentifier: identifier,
            });
          }

          throw error;
        }
      },

      register: async (data: RegisterData) => {
        set({ isLoading: true });

        try {
          // Include systemContext in register request if not already provided
          const systemContext = data.systemContext || getSystemContext();
          const response = await apiService.register({ ...data, systemContext });

          // Check if verification is needed
          const needsVerification = response.message?.includes('verification') || false;

          set({
            isLoading: false,
            needsVerification,
            pendingIdentifier: needsVerification ? data.email : null,
          });
        } catch (error: any) {
          // Check if it's a verification-related error
          const errorMsg = error?.response?.data?.message || error?.message || '';
          if (errorMsg.includes('verification') || errorMsg.includes('verify')) {
            set({
              isLoading: false,
              needsVerification: true,
              pendingIdentifier: data.email,
            });
            throw error;
          }

          // For other errors, reset state
          set({
            isLoading: false,
            needsVerification: false,
            pendingIdentifier: null,
          });
          throw error;
        }
      },

      verifyOtp: async (identifier: string, otp: string) => {
        set({ isLoading: true });

        try {
          await apiService.verifyOtp({ identifier, otp });

          set({
            isLoading: false,
            needsVerification: false,
            pendingIdentifier: null,
          });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      resendOtp: async (identifier: string) => {
        set({ isLoading: true });

        try {
          await apiService.resendOtp(identifier);
          set({ isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      loginWithGoogle: async () => {
        set({ isLoading: true });

        try {
          // Get the backend base URL from environment
          const backendBaseUrl = import.meta.env.DEV ? 'http://localhost:3001/api' : '/api';

          // Store the current URL for redirect after login
          localStorage.setItem('redirectUrl', window.location.pathname);

          // Get system context and pass it as query parameter
          const systemContext = getSystemContext();

          // Add CSRF protection using state parameter
          const oauthState = createOauthState();
          persistOauthState(oauthState);

          // Pass frontend URL to backend so it can redirect back to the correct app
          const frontendUrl = window.location.origin;
          const oauthUrl = `${backendBaseUrl}/v1/auth/google?systemContext=${systemContext}&frontendUrl=${encodeURIComponent(frontendUrl)}&state=${oauthState}`;

          // Redirect to Google OAuth with systemContext and frontendUrl
          window.location.href = oauthUrl;
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      loginWithFacebook: async () => {
        set({ isLoading: true });

        try {
          // Get the backend base URL from environment
          const backendBaseUrl = import.meta.env.DEV ? 'http://localhost:3001/api' : '/api';

          // Store the current URL for redirect after login
          localStorage.setItem('redirectUrl', window.location.pathname);

          // Get system context and pass it as query parameter
          const systemContext = getSystemContext();

          const oauthState = createOauthState();
          persistOauthState(oauthState);

          // Pass frontend URL to backend so it can redirect back to the correct app
          const frontendUrl = window.location.origin;
          const oauthUrl = `${backendBaseUrl}/v1/auth/facebook?systemContext=${systemContext}&frontendUrl=${encodeURIComponent(frontendUrl)}&state=${oauthState}`;

          // Redirect to Facebook OAuth with systemContext and frontendUrl
          window.location.href = oauthUrl;
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      registerWithGoogle: async () => {
        set({ isLoading: true });

        try {
          // Get the backend base URL from environment
          const backendBaseUrl = import.meta.env.DEV ? 'http://localhost:3001/api' : '/api';

          // Store the current URL for redirect after registration
          localStorage.setItem('redirectUrl', window.location.pathname);

          // Get system context and pass it as query parameter
          const systemContext = getSystemContext();

          const oauthState = createOauthState();
          persistOauthState(oauthState);

          // Pass frontend URL to backend so it can redirect back to the correct app
          const frontendUrl = window.location.origin;
          const oauthUrl = `${backendBaseUrl}/v1/auth/google?systemContext=${systemContext}&frontendUrl=${encodeURIComponent(frontendUrl)}&state=${oauthState}`;

          // Redirect to Google OAuth with systemContext and frontendUrl
          window.location.href = oauthUrl;
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      registerWithFacebook: async () => {
        set({ isLoading: true });

        try {
          // Get the backend base URL from environment
          const backendBaseUrl = import.meta.env.DEV ? 'http://localhost:3001/api' : '/api';

          // Store the current URL for redirect after registration
          localStorage.setItem('redirectUrl', window.location.pathname);

          // Get system context and pass it as query parameter
          const systemContext = getSystemContext();

          const oauthState = createOauthState();
          persistOauthState(oauthState);

          // Pass frontend URL to backend so it can redirect back to the correct app
          const frontendUrl = window.location.origin;
          const oauthUrl = `${backendBaseUrl}/v1/auth/facebook?systemContext=${systemContext}&frontendUrl=${encodeURIComponent(frontendUrl)}&state=${oauthState}`;

          // Redirect to Facebook OAuth with systemContext and frontendUrl
          window.location.href = oauthUrl;
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        const token = localStorage.getItem('accessToken');

        // Call backend logout endpoint if token exists
        if (token) {
          try {
            const backendBaseUrl = import.meta.env.DEV ? 'http://localhost:3001/api' : '/api';
            await fetch(`${backendBaseUrl}/v1/auth/logout`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
            });
          } catch (error) {
            // Log error but continue with local logout
            console.warn('Backend logout failed, continuing with local logout:', error);
          }
        }

        localStorage.removeItem('accessToken');
        localStorage.removeItem('redirectUrl');

        // Dọn scope state của portal khi logout (an toàn cho app khác: key không tồn tại).
        // - portalSystemContext: key legacy toàn cục, từng leak hệ thống xuyên tài khoản
        // - portalViewAs:<userId>: chế độ ADMIN xem chéo hệ, phải reset khi logout
        // Riêng portalWorkspace:<userId> GIỮ LẠI: đã key theo user (không leak được),
        // để user đăng nhập lại vẫn ở đúng tổ chức đang làm việc.
        try {
          localStorage.removeItem('portalSystemContext');
          const sweepViewAs = (storage: Storage) => {
            for (let i = storage.length - 1; i >= 0; i--) {
              const key = storage.key(i);
              if (key && key.startsWith('portalViewAs:')) {
                storage.removeItem(key);
              }
            }
          };
          sweepViewAs(sessionStorage);
          sweepViewAs(localStorage);
        } catch {
          // storage không khả dụng (Safari private...) thì bỏ qua
        }

        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          accessToken: null,
          needsVerification: false,
          pendingIdentifier: null,
        });
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      clearVerificationState: () => {
        set({
          needsVerification: false,
          pendingIdentifier: null,
        });
      },

      setUser: (user: User | null) => {
        set({ user, isAuthenticated: !!user });
      },

      setToken: (token: string | null) => {
        set({ accessToken: token });
        if (token) {
          localStorage.setItem('accessToken', token);
        } else {
          localStorage.removeItem('accessToken');
        }
      },

      hasRole: (role: UserRole): boolean => {
        const state = useAuthStore.getState();
        return state.user?.role === role;
      },

      hasPermission: (permission: string): boolean => {
        const state = useAuthStore.getState();
        const user = state.user;
        if (!user?.permissions) return false;

        // Admin has all permissions
        if (user.permissions.includes('*')) return true;

        return user.permissions.includes(permission);
      },
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: state => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        accessToken: state.accessToken,
      }),
    }
  )
);
